// Restrict the guided walkthrough without changing the app's own disabled state.
// Partition the DOM around the allowed roots, so no ancestor of a live field is
// made inert. Observe mounts only; our inert/class changes must not retrigger it.
export const lockWalkthroughInteractions = (getAllowedRoots, { blockedSelector = null } = {}) => {
  const blocked = new Map()
  const roots = () => getAllowedRoots().filter((root) => root?.isConnected)
  const isAllowed = (node) => roots().some((root) => root.contains(node))

  const restore = (node, original) => {
    if (original.inert === null) node.removeAttribute("inert")
    else node.setAttribute("inert", original.inert)
    if (!original.dimmed) node.classList.remove("therapist-walkthrough-inactive")
  }

  const update = () => {
    const allowed = roots()
    const next = new Set()
    const visit = (node) => {
      if (["SCRIPT", "STYLE", "LINK"].includes(node.tagName)) return
      if (allowed.some((root) => root === node || root.contains(node))) return
      if (allowed.some((root) => node.contains(root))) {
        Array.from(node.children).forEach(visit)
      } else {
        next.add(node)
      }
    }
    Array.from(document.body.children).forEach(visit)

    blocked.forEach((original, node) => {
      if (!next.has(node)) {
        restore(node, original)
        blocked.delete(node)
      }
    })
    next.forEach((node) => {
      if (!blocked.has(node)) {
        blocked.set(node, {
          inert: node.getAttribute("inert"),
          dimmed: node.classList.contains("therapist-walkthrough-inactive"),
        })
        node.setAttribute("inert", "")
        node.classList.add("therapist-walkthrough-inactive")
      }
    })
  }

  // Native inert covers pointer, touch, keyboard and accessibility navigation.
  // Capture guards also cover browsers without inert and synthetic activation.
  const guard = (event) => {
    if (event.type === "keydown" && event.key === "Tab") return
    const target = event.type === "submit" ? event.submitter : event.target
    // Some walkthrough targets are highlighted for display only (for example,
    // the Program Actions dropdown). Keep them visible but non-interactive.
    if (blockedSelector && target?.closest?.(blockedSelector)) {
      event.preventDefault()
      event.stopImmediatePropagation()
      return
    }
    if (target && isAllowed(target)) return
    event.preventDefault()
    event.stopImmediatePropagation()
    if (event.type === "focus" || event.type === "focusin") {
      event.target.blur?.()
    }
  }
  const events = ["click", "dblclick", "contextmenu", "keydown", "beforeinput", "input", "change", "submit", "focus", "focusin"]
  events.forEach((event) => document.addEventListener(event, guard, true))
  const observer = new MutationObserver(update)
  observer.observe(document.body, { childList: true, subtree: true })
  update()

  return () => {
    observer.disconnect()
    events.forEach((event) => document.removeEventListener(event, guard, true))
    blocked.forEach((original, node) => restore(node, original))
    blocked.clear()
  }
}
